import { useCallback, useEffect, useState } from 'react';
import { invalidateCurrentUnionRosterCache, loadCurrentUnionRoster, saveUnionMemberAvailability } from '../api/unionRosterApi.js';

const EMPTY_ROSTER = Object.freeze({ unionId: '', unionName: '', viewerRole: '', lastSyncedAt: null, members: [], activeCount: 0, siteMemberCount: 0, unlinkedCount: 0, pushMemberCount: 0, availabilityMigrationRequired: false });

export function useUnionRoster() {
  const [roster, setRoster] = useState(EMPTY_ROSTER);
  const [loading, setLoading] = useState(true);
  const [workingOpenid, setWorkingOpenid] = useState('');
  const [error, setError] = useState({ code: '', message: '' });
  const [notice, setNotice] = useState({ type: '', message: '' });

  const refresh = useCallback(async ({ force = false, quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError({ code: '', message: '' });
    try { setRoster(await loadCurrentUnionRoster({ force })); }
    catch (caught) { setRoster(EMPTY_ROSTER); setError({ code: caught?.code || 'unknown_error', message: caught instanceof Error ? caught.message : '게임 멤버 명단을 불러오지 못했습니다.' }); }
    finally { if (!quiet) setLoading(false); }
  }, []);

  const saveAvailability = useCallback(async (memberOpenid, windows) => {
    if (workingOpenid) return false;
    setWorkingOpenid(memberOpenid); setNotice({ type: '', message: '' });
    try {
      const saved = await saveUnionMemberAvailability({ memberOpenid, windows });
      setRoster((current) => ({
        ...current,
        members: current.members.map((member) => {
          if (member.memberOpenid !== saved.memberOpenid) return member;
          const sortedWindows = [...saved.availabilityWindows].sort((a, b) => a.start.localeCompare(b.start));
          return { ...member, availabilityWindows: sortedWindows, availableAt: sortedWindows[0]?.start || '' };
        }),
      }));
      setNotice({ type: 'success', message: '접속 가능 시간대를 저장했습니다.' });
      window.dispatchEvent(new CustomEvent('dororaid:union-member-availability-changed'));
      return true;
    } catch (caught) {
      setNotice({ type: 'error', message: caught instanceof Error ? caught.message : '접속시간을 저장하지 못했습니다.' });
      return false;
    } finally { setWorkingOpenid(''); }
  }, [workingOpenid]);

  useEffect(() => {
    refresh();
    const rosterChanged = () => {
      invalidateCurrentUnionRosterCache();
      refresh({ force: true, quiet: true });
    };
    const availabilityChanged = () => {
      invalidateCurrentUnionRosterCache();
    };
    const pushChanged = () => {
      invalidateCurrentUnionRosterCache();
      refresh({ force: true, quiet: true });
    };
    window.addEventListener('dororaid:union-roster-changed', rosterChanged);
    window.addEventListener('dororaid:union-member-availability-changed', availabilityChanged);
    window.addEventListener('dororaid:push-subscription-changed', pushChanged);
    return () => {
      window.removeEventListener('dororaid:union-roster-changed', rosterChanged);
      window.removeEventListener('dororaid:union-member-availability-changed', availabilityChanged);
      window.removeEventListener('dororaid:push-subscription-changed', pushChanged);
    };
  }, [refresh]);

  return { ...roster, loading, workingOpenid, error, notice, migrationRequired: error.code === 'roster_migration_required', refresh, saveAvailability };
}
